import { t } from 'src/lib/i18n'

import flashcardsIcon from '../assets/flashcards.svg'

export const findStat = (kind) => (stat) => stat?.kind === kind

export const mapDataToFooterStatsProps = (widgetType, statsData) => {
  const liveLessonStatsLabels = {
    live_lesson_total_lessons: t('total lessons'),
    live_lesson_total_exercises: t('total exercises'),
    live_lesson_exercises_feeling_good: t('feeling good'),
    live_lesson_total_hours: t('total hours'),
  }
  const mediaStatsLabels = {
    media_mastered: t('media mastered'),
    media_completed: t('media completed'),
  }
  const writingStatsLabels = {
    writing_completed: t('journals completed'),
    writing_tutor_correction: t('tutor corrections'),
  }
  const concepstStatsLabels = {
    concepts_completed: t('concepts completed'),
  }

  // statsDataPoint is map / element of statsData
  const mapLabelWithData = (labelObject) => (statsDataPoint) => {
    if (labelObject[statsDataPoint.kind]) {
      return {
        ...statsDataPoint,
        label: labelObject[statsDataPoint.kind],
        number: statsDataPoint.count,
      }
    }
  }

  if (widgetType === 'liveLesson') {
    return statsData
      .map(mapLabelWithData(liveLessonStatsLabels))
      .filter((i) => i)
  }
  if (widgetType === 'media') {
    return statsData.map(mapLabelWithData(mediaStatsLabels)).filter((i) => i)
  }
  if (widgetType === 'writing') {
    return statsData.map(mapLabelWithData(writingStatsLabels)).filter((i) => i)
  }
  if (widgetType === 'concept') {
    return statsData.map(mapLabelWithData(concepstStatsLabels)).filter((i) => i)
  }
  return []
}

export const mapDataToAchievementsProps = (data, type, countType) => {
  const last = data?.currentUser?.[type]
  const rawData = data?.currentUser?.progress?.stats
  const stats = rawData ? mapDataToFooterStatsProps(type, rawData) : []
  const activityInfo = {
    date: last?.lastSeenTimeAgo,
    cefrLevel: last?.cefrLevel,
    unitName: last?.heading,
    subTitle: last?.unit?.name,
    lessonThumbnail: last?.coverImageUrl,
  }
  const count = stats?.find((s) => s?.kind == countType)?.count || 0
  return {
    showPlaceholder: count === 0 || !last,
    linkHash: last?.identifier,
    footerStats: stats,
    widgetType: type,
    activityInfo,
  }
}

export const mapDataToAllTimeStatsProps = (progress, totalCards) => {
  const stats = progress?.currentUser?.progress?.stats
  return {
    liveLessonCount:
      stats?.find(findStat('live_lesson_total_lessons'))?.count || 0,
    flashCardCount: totalCards || 0,
    mediaCount: stats?.find(findStat('media_completed'))?.count || 0,
    writingCount: stats?.find(findStat('writing_completed'))?.count || 0,
    hoursCount: Math.floor(
      stats?.find(findStat('minutes'))?.count / 60 +
        stats?.find(findStat('live_lesson_total_hours'))?.count || 0
    ),
    conceptsCount: stats?.find(findStat('concepts_completed'))?.count || 0,
  }
}

export const mapDataToAppointmentProps = (appointment) => {
  const lastAppointment = appointment?.currentUser?.appointment
  const appointmentStatsRaw = appointment?.currentUser?.progress?.stats
  const liveLessonStats = appointmentStatsRaw
    ? mapDataToFooterStatsProps('liveLesson', appointmentStatsRaw)
    : []
  const appointmentCount =
    liveLessonStats?.find(findStat('live_lesson_total_lessons'))?.count || 0

  const smallAvatar = lastAppointment?.tutor?.avatarUrl
  const liveLessonInfo = {
    tutorAvatarUrl: smallAvatar,
    tutorLogin: lastAppointment?.tutor?.login,
    isFavorite: lastAppointment?.tutor?.tutor?.isFavorite,
    tutorName: lastAppointment?.tutor?.name,
    liveLessonReplayUrl: lastAppointment?.chatSessionArchiveUrl,
    averageStudentRating: lastAppointment?.averageStudentRating,
  }

  const activityInfoLiveLesson = {
    lessonThumbnail: lastAppointment?.exercises?.nodes[0]?.imageUrl,
    date: lastAppointment?.lastSeenTimeAgo,
    subTitle: lastAppointment?.unit?.name,
    cefrLevel: lastAppointment?.unit?.cefrLevel,
  }

  return {
    showPlaceholder: appointmentCount === 0,
    activityInfo: activityInfoLiveLesson,
    liveLessonInfo,
    footerStats: liveLessonStats,
    widgetType: 'liveLesson',
    linkHash: appointment.currentUser?.appointment?.chatSessionId,
  }
}

export const mapDataToProgressProps = (progressAndCertificationsData) => {
  const progressData = progressAndCertificationsData?.currentUser?.progress
  const cefrLevel = progressData?.cefrLevel?.code
  const cefrLevelFullName = progressData?.cefrLevel?.label
  const progressTowardsCefrLevel = progressData?.cefrLevelProgress

  let certifications = progressAndCertificationsData?.currentUser?.certifications || []

  // Prepend the current CEFR level only if it's not certified already
  // We don't want to show duplicates and we favour the completed certification
  if (!certifications.find((c) => c.cefrLevel == cefrLevel)) {
    certifications.unshift({
      cefrLevel,
      progress: progressTowardsCefrLevel,
      progressPercentage: Math.floor(progressTowardsCefrLevel * 100),
      description: cefrLevelFullName,
    })
  }

  return certifications
}

export const mapDataToCardProps = (
  flashcardStatsData,
  userProgress,
  DATASETS_STYLES
) => {
  const cardPath = flashcardStatsData?.currentUser?.progress
  const knownCards = cardPath?.knownCards
  const learningCards = cardPath?.learningCards
  const totalCards = cardPath?.totalCards[0].count
  const totalMinutesCards = cardPath?.totalMinutesCards[0].count
  const totalHoursCards = totalMinutesCards
    ? (totalMinutesCards / 60.0).toFixed(1)
    : 0

  const totalKnownCards = cardPath?.totalKnownCards[0].count
  const labelsForGraph = knownCards?.map((card) => card.time)
  let lastCardActivity = {
    ...flashcardStatsData?.currentUser?.lastCardActivity,
    lessonThumbnail: flashcardsIcon,
  }

  if (
    !flashcardStatsData?.currentUser?.lastCardActivity?.cefrLevel &&
    userProgress?.cefrLevel?.code
  ) {
    lastCardActivity = {
      ...lastCardActivity,
      date: flashcardStatsData?.currentUser?.lastCardActivity?.lastSeenTimeAgo,
      cefrLevel: userProgress?.cefrLevel?.code?.toUpperCase(),
    }
  }
  return {
    knownCards,
    lastCardActivity,
    showPlaceholder: totalCards === 0 || learningCards?.length === 0,
    totalCards,
    totalKnownCards,
    totalHours: totalHoursCards,
    labelsForGraph,
    learningCards,
    data: {
      labels: labelsForGraph,
      datasets: [
        {
          label: t('Cards known well'),
          data: knownCards?.map((card) => card.count),
          borderColor: '#AB8AEA',
          backgroundColor: 'rgba(248, 245, 254, 0.5)',
          ...DATASETS_STYLES,
        },
        {
          label: t('Cards learning'),
          data: learningCards?.map((card) => card.count),
          borderColor: '#F9DC4A',
          backgroundColor: 'rgba(254, 255, 217, 0.5)',
          ...DATASETS_STYLES,
          fill: '-1',
        },
      ],
    },
  }
}
